import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { 
  Truck, MapPin, Clock, RefreshCw, Star, 
  CheckCircle2, Timer, Package, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeliveryConfirmationWidget() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingId, setConfirmingId] = useState(null);
  const [showRatingFor, setShowRatingFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState('');
  const [itemsReceived, setItemsReceived] = useState('all');

  const fetchDeliveries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/driver-tracking/pending-deliveries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDeliveries(data.deliveries || []);
      }
    } catch (error) {
      console.error('Failed to fetch deliveries:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeliveries();
    const interval = setInterval(fetchDeliveries, 30000);
    return () => clearInterval(interval);
  }, [fetchDeliveries]);

  const handleConfirmDelivery = async (delivery) => {
    setConfirmingId(delivery.driver_id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/driver-tracking/confirm-delivery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          driver_id: delivery.driver_id,
          rating: rating,
          feedback: feedback || null,
          items_received: itemsReceived === 'all' ? 'All items received' : itemsReceived
        })
      });

      if (response.ok) {
        toast.success('Delivery confirmed! Thank you for your feedback.');
        setShowRatingFor(null);
        setRating(5);
        setFeedback('');
        setItemsReceived('all');
        fetchDeliveries();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to confirm delivery');
      }
    } catch (error) {
      toast.error('Failed to confirm delivery');
    } finally {
      setConfirmingId(null);
    }
  };

  const getEtaDisplay = (delivery) => {
    if (!delivery.eta_timestamp) return null;
    const eta = new Date(delivery.eta_timestamp);
    const now = new Date();
    const diffMs = eta - now;
    if (diffMs <= 0) return 'Arriving now';
    const diffMins = Math.ceil(diffMs / 60000);
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const pendingDeliveries = deliveries.filter(d => !d.confirmed && d.status === 'at_store');
  const incomingDeliveries = deliveries.filter(d => !d.confirmed && d.status === 'on_route');
  const confirmedDeliveries = deliveries.filter(d => d.confirmed);

  if (loading) {
    return (
      <Card className="h-full" data-testid="delivery-confirmation-widget">
        <CardContent className="flex items-center justify-center h-48">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full overflow-hidden" data-testid="delivery-confirmation-widget">
      <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            <CardTitle className="text-base font-bold">Deliveries</CardTitle>
          </div>
          <button onClick={fetchDeliveries} className="p-1 hover:bg-white/20 rounded">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex gap-3 mt-2 text-xs">
          <span className="flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> {pendingDeliveries.length} to confirm
          </span>
          <span className="flex items-center gap-1">
            <Truck className="h-3 w-3" /> {incomingDeliveries.length} incoming
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> {confirmedDeliveries.length} done
          </span>
        </div>
      </CardHeader>

      <CardContent className="p-3 space-y-3 max-h-80 overflow-y-auto">
        {/* Rating Modal */}
        {showRatingFor && (
          <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 space-y-3">
            <p className="text-sm font-medium text-orange-800">
              Rate delivery from {showRatingFor.driver_name}
            </p>
            
            {/* Star Rating */}
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <Star 
                    className={`h-6 w-6 ${
                      star <= rating 
                        ? 'text-yellow-400 fill-current' 
                        : 'text-gray-300'
                    }`} 
                  />
                </button>
              ))}
              <span className="text-sm text-slate-600 ml-2">{rating}/5</span>
            </div>
            
            {/* Items Received */}
            <div className="flex gap-2">
              <button
                onClick={() => setItemsReceived('all')}
                className={`px-3 py-1 text-xs rounded-full border ${
                  itemsReceived === 'all' 
                    ? 'bg-green-100 text-green-700 border-green-300' 
                    : 'bg-white text-slate-600 border-slate-300'
                }`}
              >
                ✓ All items received
              </button>
              <button
                onClick={() => setItemsReceived('missing')}
                className={`px-3 py-1 text-xs rounded-full border ${
                  itemsReceived === 'missing' 
                    ? 'bg-red-100 text-red-700 border-red-300' 
                    : 'bg-white text-slate-600 border-slate-300'
                }`}
              >
                ✗ Missing items
              </button>
            </div>
            
            {/* Feedback */}
            <Textarea
              placeholder="Optional feedback for the driver..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="text-sm"
              rows={2}
            />
            
            <div className="flex gap-2">
              <Button
                onClick={() => handleConfirmDelivery(showRatingFor)}
                disabled={confirmingId === showRatingFor.driver_id}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {confirmingId === showRatingFor.driver_id ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                )}
                Confirm Delivery
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowRatingFor(null);
                  setRating(5);
                  setFeedback('');
                  setItemsReceived('all');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Pending Confirmation - Driver at Store */}
        {pendingDeliveries.length > 0 && !showRatingFor && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-orange-600 uppercase tracking-wide">
              Ready to Confirm
            </p>
            {pendingDeliveries.map(delivery => (
              <div 
                key={delivery.driver_id}
                className="p-3 bg-orange-50 rounded-lg border border-orange-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                      <Truck className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{delivery.driver_name}</p>
                      <p className="text-[10px] text-slate-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> At your store now
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700">At Store</Badge>
                </div>
                <Button
                  onClick={() => setShowRatingFor(delivery)}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  size="sm"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Confirm & Rate Delivery
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Incoming Deliveries */}
        {incomingDeliveries.length > 0 && !showRatingFor && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-green-600 uppercase tracking-wide">
              On The Way
            </p>
            {incomingDeliveries.map(delivery => {
              const etaDisplay = getEtaDisplay(delivery);
              return (
                <div 
                  key={delivery.driver_id}
                  className="p-2 bg-green-50 rounded-lg border border-green-200 flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-slate-700">{delivery.driver_name}</span>
                  </div>
                  {etaDisplay && (
                    <div className="flex items-center gap-1 text-green-600">
                      <Timer className="h-3 w-3" />
                      <span className="text-xs font-medium">{etaDisplay}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Confirmed Today */}
        {confirmedDeliveries.length > 0 && !showRatingFor && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              Confirmed Today
            </p>
            {confirmedDeliveries.map(delivery => (
              <div 
                key={delivery.driver_id}
                className="p-2 bg-slate-50 rounded-lg flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-slate-600">{delivery.driver_name}</span>
                </div>
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star}
                      className={`h-3 w-3 ${
                        star <= (delivery.confirmation?.rating || 0)
                          ? 'text-yellow-400 fill-current' 
                          : 'text-gray-300'
                      }`} 
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {deliveries.length === 0 && !showRatingFor && (
          <div className="text-center py-6">
            <Package className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No deliveries today</p>
            <p className="text-xs text-slate-400">Deliveries will appear here when drivers are on route</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
